import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton, DefaultButton, TextField, Stack, Modal, IconButton } from "@fluentui/react";
import { getCompanyIcon, getLoginInfo, InfoItem } from "../../api";
import styles from "./Login.module.css";
import { APIResult } from "../../api/admin/models_admin";
import { ErrorDialog, YesNoDialog } from "../admin/components";

type LoginAPIResult = APIResult & {
    forceable?: boolean
}

const loginAPI = async (mykey: string, username: string, password: string, force: boolean = false) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    if (force) {
        formData.append('force', 'true');
    }
    const response = await fetch(`/api/login/${mykey}`, {
        method: 'POST',
        body: formData
    });
    const result: LoginAPIResult = await response.json();
    return result;
}

export const LoginPage = () => {
    const { mykey } = useParams<{ mykey: string }>();
    const urlParams = new URLSearchParams(window.location.search);
    const nextUrl = urlParams.get('next') || "/"
    const navigate = useNavigate();
    
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [companyIcon, setCompanyIcon] = useState<string>("");
    const [infoList, setInfoList] = useState<InfoItem[]>([]);
    const [infoHistory, setInfoHistory] = useState<InfoItem[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [forceLoginMessage, setForceLoginMessage] = useState<null|string>(null)
    const [errorMessage, setErrorMessage] = useState<null|string>(null)

    useEffect(() => {
        // ローカルストレージの処理
        const pathArray = window.location.pathname.split('/');
        
        if (pathArray[pathArray.length - 1] === 'login') {
            const value = localStorage.getItem('WorQuforGAI_companykey');
            if (value) {
                navigate("/login/" + value);
                localStorage.removeItem('WorQuforGAI_companykey');
                return;
            }
            navigate("/badroute");
            return;
        }

        for (let index = 0; index < pathArray.length; index++) {
            if (pathArray[index] === 'login' && index < pathArray.length - 1) {
                const key = pathArray[index + 1];
                if (key.length <= 0) {
                    navigate("/badroute")
                    return;
                }
                localStorage.setItem('WorQuforGAI_companykey', key);
            }
        }

        // 会社アイコン取得
        const fetchCompanyIcon = async () => {
            try {
                const objectURL = await getCompanyIcon();
                setCompanyIcon(objectURL);
            } catch (error) {
                console.error("Failed to load company icon:", error);
            }
        };
        fetchCompanyIcon();

        // お知らせ情報の取得
        const fetchLoginInfo = async () => {
            if (mykey) {
                try {
                    const data = await getLoginInfo();
                    setInfoList(data.info_list);
                    setInfoHistory(data.info_history);
                } catch (error) {
                    console.error("Failed to load login info:", error);
                }
            }
        };
        fetchLoginInfo();
    }, [mykey]);

    const dismissForceLoginDialog = () => {
        setForceLoginMessage(null)
    }
    const dismissErrorDialog = () => {
        setErrorMessage(null)
    }

    const confirmForceLogin = async () => {
        try{
            if (!mykey) return;
            const forceResult = await loginAPI(mykey, username, password, true)
            if (forceResult.success) {
                navigate(nextUrl);
            } else {
                setErrorMessage(forceResult.message || 'ログインに失敗しました。');
            }
        }
        finally{
            dismissForceLoginDialog()
        }
    }

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        
        if (isLoading) return;
        if (!mykey) return;
        setIsLoading(true);

        try {
            const result = await loginAPI(mykey, username, password)
            const urlParams = new URLSearchParams(window.location.search);
            const nextUrl = urlParams.get('next') || "/";

            if (result.success) {
                navigate(nextUrl);
            } else if (result.forceable) {
                setForceLoginMessage((result.message ?? "").replace('/n', '\n'));
            } else {
                setErrorMessage(result.message || 'ログインに失敗しました。');
            }
        } catch (error) {
            console.error('ログインエラー:', error);
            setErrorMessage('ログイン処理中にエラーが発生しました。');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePassword = () => {
        navigate(`/change_password/${mykey}`);
    };

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    return (
        <div className={styles.loginPage}>
            <h2 className={styles.pageTitle}>WorQu for GAI</h2>
            <h3 className={styles.pageSubtitle}>powered by ChatGPT</h3>
            
            <Stack horizontalAlign="center" className={styles.container}>
                <div className={styles.logoContainer}>
                    {companyIcon && (
                        <img src={companyIcon} alt="Logo" className={styles.headerIcon} aria-hidden="true" />
                    )}
                </div>
                
                <form onSubmit={handleLogin} style={{width: "100%"}}>
                    <div className={styles.idpassContainer}>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <TextField
                                label="ユーザーID"
                                value={username}
                                onChange={(_, newValue) => setUsername(newValue || "")}
                                required
                                autoComplete="username"
                            />
                            <TextField
                                label="パスワード"
                                type="password"
                                value={password}
                                onChange={(_, newValue) => setPassword(newValue || "")}
                                required
                                canRevealPassword
                                autoComplete="current-password"
                            />
                        </Stack>
                    </div>

                    <Stack className={styles.buttonContainer} tokens={{childrenGap: "15px"}}>
                        <PrimaryButton 
                            type="submit" 
                            className={styles.loginButton}
                            disabled={isLoading}
                        >
                            ログイン
                        </PrimaryButton>
                        <DefaultButton 
                            className={styles.loginButton}
                            onClick={handleChangePassword}
                            disabled={isLoading}
                        >
                            パスワード変更
                        </DefaultButton>
                    </Stack>
                </form>
            </Stack>
            <div>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.manualLink}
                    href="https://dsk-newtech.com/worqugai_support.html"
                >
                    マニュアル・利用規約はこちら
                </a>
            </div>
            {infoList.length > 0 && (
                <div className={styles.infoContainer}>
                    <ul className={styles.infoList}>
                        {infoList.map((info, index) => (
                            <li key={index} className={styles.infoItem}>
                                <p className={styles.infoDate}>{info.start_date}</p>
                                <p className={styles.infoContent} dangerouslySetInnerHTML={{ __html: info.content }} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {infoHistory.length > 0 && (
                <>
                    <div className={styles.releaseHistory}>
                        <span onClick={openModal}>
                            過去のリリース履歴はこちら
                        </span>
                    </div>

                    <Modal
                        isOpen={showModal}
                        onDismiss={closeModal}
                        isBlocking={false}
                        containerClassName={styles.modalContainer}
                    >
                        <div className={styles.modalHeader}>
                            <span className={styles.modalTitle}>リリース履歴</span>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                ariaLabel="閉じる"
                                onClick={closeModal}
                            />
                        </div>
                        <div className={styles.historyContainer}>
                            <ul className={styles.infoList}>
                                {infoHistory.map((info, index) => (
                                    <li key={index} className={styles.infoItem}>
                                        <p className={styles.infoDate}>{info.start_date}</p>
                                        <p className={styles.infoContent} dangerouslySetInnerHTML={{ __html: info.content }} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Modal>
                </>
            )}
            <ErrorDialog
                isOpen={errorMessage !== null}
                onDismiss={dismissErrorDialog}
                content={errorMessage ?? ""}
            />
            <YesNoDialog
                title="確認"
                isOpen={forceLoginMessage !== null}
                onConfirm={confirmForceLogin}
                onDismiss={dismissForceLoginDialog}
                content={forceLoginMessage ?? ""}
            />
        </div>
    );
}